import React, { useState } from "react";
import { toast } from "sonner";
import { FormCard } from "@/components/FormCard";
import { ContentPageTheme, Form, ReviewPageConfig, ReviewPageConfigServices } from "@/services/bedrock";
import { LiquidPlaceholderGroups } from "@/utils/liquidHelpers";
import { LiquidTextField } from "../../components/LiquidTextField";
import { SaveCancelActions } from "../../components/SaveCancelActions";
import { SettingGroup, SettingRow, SortSettingRow } from "../../components/SettingRow";
import { ThemeAndFormPickers } from "../../components/ThemeAndFormPickers";
import { useSavedConfig } from "../../components/useSavedConfig";
import {
  TEXT_MAX_LENGTH,
  LiquidField,
  LiquidFieldErrors,
  getReviewPageSettings,
  liquidFieldErrors,
} from "./reviewPageSettings";

type ToggleKey =
  | "showStarSummary"
  | "showSentimentSummary"
  | "showNpsSummary"
  | "useCondensedQuotes"
  | "approvedForSocialOnly"
  | "showReviewReplies"
  | "showSourceFilter"
  | "showOfficeDetails"
  | "showContactButton"
  | "showSocialLinks";

interface Props {
  config: ReviewPageConfig;
  updateConfig: (updates: Partial<ReviewPageConfig>) => void;
  themes: ContentPageTheme[];
  forms: Form[];
  placeholders: LiquidPlaceholderGroups;
}

const ReviewPageConfigForm: React.FC<Props> = ({ config, updateConfig, themes, forms, placeholders }) => {
  const [fieldErrors, setFieldErrors] = useState<LiquidFieldErrors>({});

  const { hasChanges, isSaving, handleCancel, handleSave } = useSavedConfig({
    config,
    getEditable: getReviewPageSettings,
    updateConfig,
    save: settings => ReviewPageConfigServices.updateConfig(settings),
    savedMessage: "Reviews page settings saved",
    errorMessage: "Error saving reviews page settings",
    onError: error => {
      const errors = liquidFieldErrors(error);
      if (Object.keys(errors).length) setFieldErrors(errors);
      else toast.error("Error saving reviews page settings");
    },
  });

  const setText = (field: LiquidField, value: string) => {
    updateConfig({ [field]: value });
    setFieldErrors(current => ({ ...current, [field]: undefined }));
  };

  const toggle = (key: ToggleKey, label: string, tooltip?: string) => (
    <SettingRow
      id={`review-page-${key}`}
      label={label}
      tooltip={tooltip}
      checked={config[key]}
      onCheckedChange={checked => updateConfig({ [key]: checked })}
    />
  );

  return (
    <FormCard>
      <FormCard.Header
        title="Configurations"
        description="Configure and preview the public reviews page for each Player."
      />

      <FormCard.Content>
        <LiquidTextField
          id="review-page-headline"
          label="Headline"
          value={config.headline ?? ""}
          onChange={value => setText("headline", value)}
          placeholders={placeholders}
          placeholder="Player's name"
          maxLength={TEXT_MAX_LENGTH}
          error={fieldErrors.headline}
        />
        <LiquidTextField
          id="review-page-subtitle"
          label="Subtitle"
          value={config.subtitle ?? ""}
          onChange={value => setText("subtitle", value)}
          placeholders={placeholders}
          placeholder="Title · Company · Compliance disclaimer"
          maxLength={TEXT_MAX_LENGTH}
          error={fieldErrors.subtitle}
        />

        <div className="grid gap-4 @min-[35rem]:grid-cols-2">
          <SettingGroup title="Summaries">
            {toggle(
              "showStarSummary",
              "Star summary",
              "Hidden on the page if the Player's average star rating is below 4.0."
            )}
            {toggle(
              "showSentimentSummary",
              "Sentiment summary",
              "Hidden on the page if fewer than 70% of reviews are positive."
            )}
            {toggle("showNpsSummary", "NPS summary", "Hidden on the page if the Player's NPS is below 50.")}
          </SettingGroup>

          <SettingGroup title="Reviews">
            {toggle("useCondensedQuotes", "Condensed quotes")}
            {toggle("approvedForSocialOnly", "Approved for social only")}
            {toggle("showReviewReplies", "Player replies")}
            {toggle(
              "showSourceFilter",
              "Source filter",
              "Lets visitors filter reviews by Google, Facebook and other sources."
            )}
            <SortSettingRow
              value={config.reviewSortType}
              onChange={reviewSortType => updateConfig({ reviewSortType })}
            />
          </SettingGroup>

          <SettingGroup title="Page sections">
            {toggle(
              "showOfficeDetails",
              "Office details",
              "Map, hours, address and phone from the Player's Google Business Profile."
            )}
            {toggle(
              "showContactButton",
              "Contact button",
              "Links to the Player's card, or their website when the card isn't public."
            )}
            {toggle("showSocialLinks", "Social links")}
          </SettingGroup>
        </div>

        <ThemeAndFormPickers
          themes={themes}
          forms={forms}
          contentPageThemeId={config.contentPageThemeId}
          formId={config.formId}
          onChange={updateConfig}
        />
      </FormCard.Content>

      <SaveCancelActions
        hasChanges={hasChanges}
        isSaving={isSaving}
        onCancel={() => {
          setFieldErrors({});
          handleCancel();
        }}
        onSave={handleSave}
      />
    </FormCard>
  );
};

export { ReviewPageConfigForm };
